type ShortcutPlatform = 'linux' | 'mac' | 'windows';

export type DisallowedShortcut = {
  combo: string;
  reason: string;
  platforms?: readonly ShortcutPlatform[];
  browserOnly?: boolean;
};

const comboKeyAliases: Record<string, string> = {
  ',': 'comma',
  '-': 'minus',
  '.': 'period',
  '=': 'equal',
  '[': 'bracketleft',
  ']': 'bracketright',
  cmd: 'meta',
  command: 'meta',
  control: 'ctrl',
  esc: 'escape',
  option: 'alt',
};

export const DISALLOWED_SHORTCUTS: readonly DisallowedShortcut[] = [
  { combo: 'mod+c', reason: 'copy' },
  { combo: 'mod+v', reason: 'paste' },
  { combo: 'mod+x', reason: 'cut' },
  { combo: 'mod+a', reason: 'select all' },
  { combo: 'mod+z', reason: 'undo' },
  { combo: 'mod+shift+z', reason: 'redo' },
  { combo: 'mod+y', reason: 'redo' },
  { combo: 'mod+p', reason: 'print' },
  { combo: 'mod+s', reason: 'save', browserOnly: true },
  { combo: 'mod+f', reason: 'find' },
  { combo: 'mod+n', reason: 'new window or document' },
  { combo: 'mod+o', reason: 'open file' },
  { combo: 'mod+w', reason: 'close window or tab' },
  { combo: 'mod+t', reason: 'new tab' },
  { combo: 'meta+h', reason: 'macOS hide app', platforms: ['mac'] },
  { combo: 'meta+m', reason: 'macOS minimize window', platforms: ['mac'] },
  { combo: 'meta+q', reason: 'macOS quit app', platforms: ['mac'] },
  { combo: 'meta+tab', reason: 'macOS app switcher', platforms: ['mac'] },
  { combo: 'meta+space', reason: 'Spotlight', platforms: ['mac'] },
  { combo: 'meta+shift+space', reason: 'Spotlight', platforms: ['mac'] },
  { combo: 'meta+shift+3', reason: 'macOS screenshot', platforms: ['mac'] },
  { combo: 'meta+shift+4', reason: 'macOS screenshot', platforms: ['mac'] },
  { combo: 'meta+alt+escape', reason: 'macOS Force Quit', platforms: ['mac'] },
  {
    combo: 'meta+ctrl+space',
    reason: 'macOS emoji picker',
    platforms: ['mac'],
  },
  { combo: 'alt+f4', reason: 'close window', platforms: ['windows', 'linux'] },
  {
    combo: 'ctrl+shift+escape',
    reason: 'Windows Task Manager',
    platforms: ['windows'],
  },
];

function getShortcutPlatform(): ShortcutPlatform {
  const platform =
    typeof window === 'undefined' ? undefined : window.systemInfo?.platform;
  if (platform === 'darwin') return 'mac';
  if (platform === 'win32') return 'windows';
  if (platform === 'linux') return 'linux';

  if (typeof navigator === 'undefined') return 'linux';
  if (navigator.platform.includes('Mac')) return 'mac';
  if (navigator.platform.includes('Win')) return 'windows';
  return 'linux';
}

function isElectronRuntime(): boolean {
  return typeof window !== 'undefined' && typeof window.authApi !== 'undefined';
}

function normalizeComboKey(key: string, platform: ShortcutPlatform): string {
  const normalizedKey = key.trim().toLowerCase().replace(/^\$/, '');
  if (normalizedKey === 'mod') return platform === 'mac' ? 'meta' : 'ctrl';
  return comboKeyAliases[normalizedKey] ?? normalizedKey;
}

export function normalizeShortcutCombo(combo: string): string {
  const platform = getShortcutPlatform();
  return combo
    .split('+')
    .map((key) => normalizeComboKey(key, platform))
    .filter(Boolean)
    .sort()
    .join('+');
}

export function getDisallowedShortcut(
  combo: string
): DisallowedShortcut | undefined {
  const platform = getShortcutPlatform();
  const normalizedCombo = normalizeShortcutCombo(combo);

  return DISALLOWED_SHORTCUTS.find((shortcut) => {
    if (shortcut.browserOnly && isElectronRuntime()) {
      return false;
    }

    if (shortcut.platforms && !shortcut.platforms.includes(platform)) {
      return false;
    }

    return normalizeShortcutCombo(shortcut.combo) === normalizedCombo;
  });
}

export function getDisallowedShortcutMessage(
  shortcut: DisallowedShortcut
): string {
  return `This shortcut is reserved for ${shortcut.reason}. Try a different combination.`;
}
